// Packing Houses (migration 0080) — pure resolvers + the two row mappers.
//
// A Packing House is the place a lot is handed to; its members share one pool
// of material. Membership is many-to-many (a packager may work in several
// houses). These helpers answer "which houses does this person work in" and
// "who works here" from the two context arrays, with no I/O.
//
// House mode vs legacy mode: the app runs in HOUSE MODE as soon as at least
// one house exists (the migration backfills a house of one per packager, so
// after it every Supabase site is in house mode). With no houses at all —
// Firestore mode, or a Supabase site before 0080 — every page keeps the old
// one-person-per-grant behaviour untouched.
import { UserRole } from '../types';
import type { PackingHouse, PackingHouseMember, User } from '../types';
import { isPackagingStaffRole } from './warehousePolicy';

export const houseModeActive = (houses: readonly PackingHouse[] | undefined | null): boolean =>
  !!houses && houses.length > 0;

/** Houses `userId` is a member of, active first, then by name. */
export function housesForUser(
  userId: string | null | undefined,
  houses: readonly PackingHouse[],
  members: readonly PackingHouseMember[],
): PackingHouse[] {
  if (!userId) return [];
  const mine = new Set(members.filter((m) => m.userId === userId).map((m) => m.houseId));
  return sortHouses(houses.filter((h) => mine.has(h.id)));
}

/** Member user rows of `houseId`, in `users` order; archived users included
 *  so a house never looks emptier than its table says. */
export function membersOfHouse(
  houseId: string | null | undefined,
  users: readonly User[],
  members: readonly PackingHouseMember[],
): User[] {
  if (!houseId) return [];
  const ids = new Set(members.filter((m) => m.houseId === houseId).map((m) => m.userId));
  return users.filter((u) => ids.has(u.id));
}

export function isHouseMember(
  userId: string | null | undefined,
  houseId: string | null | undefined,
  members: readonly PackingHouseMember[],
): boolean {
  if (!userId || !houseId) return false;
  return members.some((m) => m.houseId === houseId && m.userId === userId);
}

/** Which houses a viewer may act in on the Packaging House page: a packager
 *  their own, the WM tier (warehouse manager / manager / admin / super admin)
 *  every active house. */
export function housesForViewer(
  user: Pick<User, 'id' | 'role'> | null | undefined,
  houses: readonly PackingHouse[],
  members: readonly PackingHouseMember[],
): PackingHouse[] {
  if (!user) return [];
  if (isPackagingStaffRole(user.role)) return housesForUser(user.id, houses, members);
  return sortHouses(houses.filter((h) => h.isActive));
}

/** Roles that can be a house member — the packaging staff proper. */
export const canJoinPackingHouse = (role: UserRole | undefined | null): boolean => isPackagingStaffRole(role);

/** Active houses first, then by name — the order every picker shows. */
export function sortHouses(houses: readonly PackingHouse[]): PackingHouse[] {
  return [...houses].sort((a, b) =>
    (Number(b.isActive) - Number(a.isActive)) || a.name.localeCompare(b.name));
}

/** The pool a ledger row belongs to: the house when it has one, else the
 *  person. What the Warehouse hub groups and filters by in house mode. */
export function holderOf(row: { workerId: string; workerName: string; houseId?: string; houseName?: string }): { id: string; name: string } {
  return row.houseId
    ? { id: row.houseId, name: row.houseName || row.workerName }
    : { id: row.workerId, name: row.workerName };
}

/** Row mapper for `packing_houses`. */
export const sbPackingHouseFromRow = (r: any): PackingHouse => {
  const h: PackingHouse = {
    id: r.id,
    name: r.name,
    isActive: r.is_active !== false,
    createdAt: r.created_at ? Date.parse(r.created_at) : 0,
  };
  if (r.address != null) h.address = r.address;
  if (r.notes != null) h.notes = r.notes;
  if (r.created_by != null) h.createdBy = r.created_by;
  return h;
};

/** Row mapper for `packing_house_members`. */
export const sbPackingHouseMemberFromRow = (r: any): PackingHouseMember => ({
  houseId: r.house_id,
  userId: r.user_id,
  assignedBy: r.assigned_by ?? null,
  assignedAt: r.assigned_at ? Date.parse(r.assigned_at) : 0,
});
